// Close codes of the chat socket (backend CHAT_SOCKET_CLOSE).
export const SOCKET_CLOSE = {
  reconnect: 4000,
  unauthorized: 4401,
  originNotAllowed: 4403,
  overCapacity: 4429,
} as const;

const OPEN = 1;
const MAX_FAILURES = 2;

/** `socket`: live; `polling`: the socket failed twice or the member is invisible. */
export type InboxConnection = 'connecting' | 'socket' | 'polling' | 'off';

export interface ServerFrame {
  type: string;
  id?: string | null;
  at?: string;
  data?: unknown;
}

export type ClientFrame =
  | { type: 'subscribe'; conversations: Array<{ id: string; after?: string }> }
  | { type: 'unsubscribe'; conversationIds: string[] }
  | { type: 'typing'; conversationId: string; internal?: boolean }
  | { type: 'read'; conversationId: string; upToMessageId: string }
  | { type: 'presence'; status: 'online' | 'away' }
  | { type: 'ping' };

/** The parts of the browser WebSocket the transport uses; tests pass a fake. */
export interface SocketLike {
  readonly readyState: number;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type SocketConstructor = new (url: string) => SocketLike;

export interface InboxSocketOptions {
  WebSocket: SocketConstructor | undefined;
  /** A fresh single-use URL (ticket included) from POST /admin/v1/chat/ws-ticket. */
  socketUrl: () => Promise<string>;
  onFrame: (frame: ServerFrame) => void;
  /** Accepted: resend subscriptions and presence. */
  onReady: () => void;
  onMode: (mode: InboxConnection) => void;
  socketRetryMs?: number;
  pingIntervalMs?: number;
}

export interface InboxSocket {
  start(): void;
  /** False while no socket is live; the caller then relies on REST and polling. */
  send(frame: ClientFrame): boolean;
  stop(): void;
}

/**
 * One socket for the inbox (ADR 87, staff side). A socket that never reaches `ready` or is refused
 * (4401, 4403, 4429) is a failure; after two in a row the inbox polls and the socket is tried
 * again after `socketRetryMs`. 4000 is the server's 30-minute recycle and reconnects at once, as
 * does a live socket that drops. Pings every 25 s; two missed pongs close it.
 */
export function createInboxSocket(options: InboxSocketOptions): InboxSocket {
  const retryMs = options.socketRetryMs ?? 5 * 60_000;
  const pingMs = options.pingIntervalMs ?? 25_000;
  let socket: SocketLike | null = null;
  let ready = false;
  let stopped = true;
  let failures = 0;
  let mode: InboxConnection = 'off';
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let pingTimer: ReturnType<typeof setInterval> | undefined;
  let missedPongs = 0;
  const isStopped = () => stopped;

  const setMode = (next: InboxConnection) => {
    if (next === mode) return;
    mode = next;
    options.onMode(next);
  };

  const clearPing = () => {
    clearInterval(pingTimer);
    pingTimer = undefined;
  };

  const startPolling = () => {
    setMode('polling');
    clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      if (stopped) return;
      failures = MAX_FAILURES - 1;
      void connect();
    }, retryMs);
  };

  const failed = () => {
    failures += 1;
    if (failures >= MAX_FAILURES) startPolling();
    else void connect();
  };

  function sendRaw(frame: ClientFrame): boolean {
    if (!socket || !ready || socket.readyState !== OPEN) return false;
    socket.send(JSON.stringify(frame));
    return true;
  }

  async function connect(): Promise<void> {
    if (stopped) return;
    if (!options.WebSocket) {
      startPolling();
      return;
    }
    if (mode !== 'polling') setMode('connecting');
    let url: string;
    try {
      url = await options.socketUrl();
    } catch {
      if (!isStopped()) failed();
      return;
    }
    if (isStopped()) return;
    let current: SocketLike;
    try {
      current = new options.WebSocket(url);
    } catch {
      failed();
      return;
    }
    socket = current;
    ready = false;
    current.onmessage = (event) => {
      if (socket !== current || typeof event.data !== 'string') return;
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data) as ServerFrame;
      } catch {
        return;
      }
      if (frame.type === 'ready') {
        ready = true;
        failures = 0;
        clearTimeout(retryTimer);
        setMode('socket');
        missedPongs = 0;
        clearPing();
        pingTimer = setInterval(() => {
          if (missedPongs >= 2) {
            current.close();
            return;
          }
          missedPongs += 1;
          sendRaw({ type: 'ping' });
        }, pingMs);
        options.onReady();
        return;
      }
      if (frame.type === 'pong') {
        missedPongs = 0;
        return;
      }
      options.onFrame(frame);
    };
    current.onclose = (event) => {
      if (socket !== current) return;
      socket = null;
      clearPing();
      const wasReady = ready;
      ready = false;
      if (stopped) return;
      if (event.code === SOCKET_CLOSE.reconnect) {
        void connect();
        return;
      }
      const refused =
        event.code === SOCKET_CLOSE.unauthorized ||
        event.code === SOCKET_CLOSE.originNotAllowed ||
        event.code === SOCKET_CLOSE.overCapacity;
      if (wasReady && !refused) {
        void connect();
        return;
      }
      failed();
    };
    current.onerror = () => undefined;
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      failures = 0;
      void connect();
    },
    send: sendRaw,
    stop() {
      stopped = true;
      clearTimeout(retryTimer);
      clearPing();
      const current = socket;
      socket = null;
      ready = false;
      current?.close(1000, 'client closed');
      setMode('off');
    },
  };
}
